/**
 * ESS Cookie Consent Component
 *
 * A GDPR-compliant cookie consent banner that integrates with Google Analytics.
 * When users accept cookies, Google Analytics is initialized.
 * When users reject cookies, Google Analytics is disabled and tracking cookies are removed.
 *
 * Features:
 * - Two-step consent flow (simple accept/reject + detailed settings)
 * - Google Analytics 4 integration
 * - Cookie management with expiration
 * - Persistent consent storage
 * - Internationalization support
 * - Accessible modal interface
 *
 * @module components/Organisms/CookieConsent
 */

import React, { useState, useEffect, useMemo } from 'react';
import config from '@plone/volto/registry';
import { useCookies } from 'react-cookie';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Button } from '../../Atoms/ESSButton/Button';
import { Checkbox } from '../../Atoms/ESSCheckbox/Checkbox';
import { ContentWrapper, PageSection } from 'volto-essvoltocomponents/components/Layout';
import RichText from '../../Atoms/RichText';
import './CookieConsent.scss';

const messages = defineMessages({
  privacyNotice: {
    id: 'Privacy Notice',
    defaultMessage: 'Privacy Notice',
  },
  technicallyRequired: {
    id: 'Technically required cookies',
    defaultMessage: 'Technically required cookies',
  },
  analyticsTracking: {
    id: 'Analytics and tracking',
    defaultMessage: 'Analytics and tracking',
  },
  acceptAll: {
    id: 'Accept all cookies',
    defaultMessage: 'Accept all cookies',
  },
  rejectAll: {
    id: 'Reject all',
    defaultMessage: 'Reject all',
  },
  customizeSettings: {
    id: 'Customize settings',
    defaultMessage: 'Customize settings',
  },
  savePreferences: {
    id: 'Save preferences',
    defaultMessage: 'Save preferences',
  },
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  technicalCookiesDescription: {
    id: 'These cookies are essential for the website to function properly. They cannot be disabled.',
    defaultMessage:
      'These cookies are essential for the website to function properly. They cannot be disabled.',
  },
  analyticsCookiesDescription: {
    id: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    defaultMessage:
      'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
  },
});

/**
 * Initialize Google Analytics 4
 * @param {string} measurementId - Google Analytics 4 measurement ID (G-XXXXXXXXXX)
 */
const initializeGoogleAnalytics = (measurementId) => {
  if (!measurementId || typeof window === 'undefined') return;

  // Check if already initialized to prevent duplicate scripts
  if (window.gaInitialized) {
    console.log('[CookieConsent] Google Analytics already initialized');
    return;
  }

  // Initialize dataLayer BEFORE loading the script (this is the correct order per Google's docs)
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  // Queue the initial commands - they will be processed when script loads
  gtag('js', new Date());

  // Configure GA4 with appropriate cookie settings for localhost
  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  // For localhost, we need to explicitly set cookie_domain to 'none' to allow cookies
  const cookieConfig = {
    anonymize_ip: true,
    // Allow cookies on localhost by setting cookie_domain to 'none'
    cookie_domain: isLocalhost ? 'none' : 'auto',
    // Send page view immediately
    send_page_view: true,
  };

  gtag('config', measurementId, cookieConfig);

  // Now load the gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

  script.onload = () => {
    console.log('[CookieConsent] gtag.js script loaded successfully');
    // Send a test event to verify tracking is working
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
      console.log('[CookieConsent] Test page_view event sent');
    }
  };

  script.onerror = () => {
    console.error('[CookieConsent] Failed to load gtag.js script');
  };

  document.head.appendChild(script);
  window.gaInitialized = true;

  console.log('[CookieConsent] Google Analytics initialized:', measurementId);
};

/**
 * Disable Google Analytics and remove tracking cookies
 * @param {string} measurementId - Google Analytics 4 measurement ID
 */
const disableGoogleAnalytics = (measurementId, removeCookie) => {
  if (typeof window === 'undefined') return;

  // Set GA disable flag
  if (measurementId) {
    window[`ga-disable-${measurementId}`] = true;
  }

  // Remove GA cookies
  const cookieOptions = { path: '/', domain: window.location.hostname };
  removeCookie('_ga', cookieOptions);
  removeCookie('_gid', cookieOptions);
  removeCookie('_gat', cookieOptions);
  removeCookie(`_ga_${measurementId?.replace('G-', '')}`, cookieOptions);

  console.log('[CookieConsent] Google Analytics disabled and cookies removed');
};

/**
 * CookieConsent Component
 *
 * @param {Object} props
 * @param {string} props.googleAnalyticsId - Google Analytics 4 measurement ID (e.g., 'G-XXXXXXXXXX')
 * @param {string} props.privacyPolicyUrl - URL to privacy policy page
 * @param {number} props.cookieExpiryMonths - Number of months before consent expires (default: 6)
 * @param {boolean} props.showOnLoad - Force show banner on component mount (for testing)
 */
/**
 * Get GA4 ID from multiple sources (in order of priority):
 * 1. Props passed directly to component
 * 2. Volto config settings
 * 3. window.env (runtime environment variables in browser)
 * 4. process.env (build-time environment variables)
 */
const getGoogleAnalyticsId = (propValue) => {
  // Debug: Log all possible sources
  if (typeof window !== 'undefined') {
    console.log('[CookieConsent] GA4 ID sources:', {
      propValue,
      configSettings: config?.settings?.googleAnalyticsId,
      windowEnv: window.env?.RAZZLE_GA4_CODE,
      processEnv:
        typeof process !== 'undefined'
          ? process.env?.RAZZLE_GA4_CODE
          : undefined,
    });
  }

  // 1. If explicitly passed as prop, use it
  if (propValue) return propValue;

  // 2. Check Volto config settings
  if (config?.settings?.googleAnalyticsId) {
    return config.settings.googleAnalyticsId;
  }

  // 3. Check window.env (runtime env vars in browser - Volto's way)
  if (typeof window !== 'undefined' && window.env?.RAZZLE_GA4_CODE) {
    return window.env.RAZZLE_GA4_CODE;
  }

  // 4. Fallback to process.env (build-time)
  if (typeof process !== 'undefined' && process.env?.RAZZLE_GA4_CODE) {
    return process.env.RAZZLE_GA4_CODE;
  }

  return null;
};

const CookieConsent = ({
  googleAnalyticsId: propGoogleAnalyticsId,
  privacyPolicyUrl = '/privacy-policy',
  cookieExpiryMonths = 6,
  showOnLoad = false,
}) => {
  const intl = useIntl();

  // Get GA4 ID using the helper function
  const googleAnalyticsId = useMemo(
    () => getGoogleAnalyticsId(propGoogleAnalyticsId),
    [propGoogleAnalyticsId],
  );
  const [cookies, setCookie, removeCookie] = useCookies([
    'ess_cookie_consent',
    'ess_analytics_consent',
  ]);

  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  // Check if user has already given consent
  useEffect(() => {
    const hasConsent = cookies.ess_cookie_consent === 'true';
    const hasAnalyticsConsent = cookies.ess_analytics_consent === 'true';

    if (!hasConsent || showOnLoad) {
      setShowBanner(true);
    }

    if (hasAnalyticsConsent && googleAnalyticsId) {
      initializeGoogleAnalytics(googleAnalyticsId);
      setAnalyticsEnabled(true);
    }
  }, [cookies, googleAnalyticsId, showOnLoad]);

  // Calculate cookie expiry date
  const getCookieExpiry = () => {
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + cookieExpiryMonths);
    return expiryDate;
  };

  const cookieOptions = {
    path: '/',
    expires: getCookieExpiry(),
    sameSite: 'lax',
  };

  /**
   * Handle "Accept All" - Enable all cookies including analytics
   */
  const handleAcceptAll = () => {
    setCookie('ess_cookie_consent', 'true', cookieOptions);
    setCookie('ess_analytics_consent', 'true', cookieOptions);

    if (googleAnalyticsId) {
      initializeGoogleAnalytics(googleAnalyticsId);
    }

    setShowBanner(false);
    setShowSettings(false);
  };

  /**
   * Handle "Reject All" - Only essential cookies
   */
  const handleRejectAll = () => {
    setCookie('ess_cookie_consent', 'true', cookieOptions);
    setCookie('ess_analytics_consent', 'false', cookieOptions);

    if (googleAnalyticsId) {
      disableGoogleAnalytics(googleAnalyticsId, removeCookie);
    }

    setShowBanner(false);
    setShowSettings(false);
  };

  /**
   * Handle "Save Preferences" - Save custom settings
   */
  const handleSavePreferences = () => {
    setCookie('ess_cookie_consent', 'true', cookieOptions);
    setCookie(
      'ess_analytics_consent',
      analyticsEnabled.toString(),
      cookieOptions,
    );

    if (analyticsEnabled && googleAnalyticsId) {
      initializeGoogleAnalytics(googleAnalyticsId);
    } else if (!analyticsEnabled && googleAnalyticsId) {
      disableGoogleAnalytics(googleAnalyticsId, removeCookie);
    }

    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <aside
      className={`ess-cookie-consent ess-cookie-consent--sticky${
        showSettings ? ' ess-cookie-consent--expanded' : ''
      }`}
      role="region"
      aria-label={intl.formatMessage(messages.privacyNotice)}
    >
    <PageSection className="ess-cookie-consent__inner">
        {!showSettings ? (
          // Simple consent view
          <ContentWrapper className="ess-cookie-consent__simple">
            <h2 className="ess-cookie-consent__title">
              {intl.formatMessage(messages.privacyNotice)}
            </h2>

            <div className="ess-cookie-consent__content">
              <p>
                <FormattedMessage
                  id="We use cookies to improve your experience on our website. Essential cookies are required for the site to function, while analytics cookies help us understand how you use our site."
                  defaultMessage="We use cookies to improve your experience on our website. Essential cookies are required for the site to function, while analytics cookies help us understand how you use our site."
                />
              </p>
              <p>
                <FormattedMessage
                  id="You can accept all cookies, reject optional cookies, or customize your preferences. For more information, please read our {privacyPolicy}."
                  defaultMessage="You can accept all cookies, reject optional cookies, or customize your preferences. For more information, please read our {privacyPolicy}."
                  values={{
                    privacyPolicy: (
                      <a
                        href={privacyPolicyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ess-cookie-consent__link"
                      >
                        <FormattedMessage
                          id="Privacy Policy"
                          defaultMessage="Privacy Policy"
                        />
                      </a>
                    ),
                  }}
                />
              </p>
            </div>

            <div className="ess-cookie-consent__actions">
              <Button
                variant="positive"
                onClick={handleAcceptAll}
                className="ess-cookie-consent__button"
              >
                {intl.formatMessage(messages.acceptAll)}
              </Button>
              <Button
                variant="negative"
                onClick={handleRejectAll}
                className="ess-cookie-consent__button"
              >
                {intl.formatMessage(messages.rejectAll)}
              </Button>
            </div>
          </ContentWrapper>
        ) : (
          // Detailed settings view
          <ContentWrapper className="ess-cookie-consent__settings">
            <h2 className="ess-cookie-consent__title">
              {intl.formatMessage(messages.privacyNotice)}
            </h2>

            <div className="ess-cookie-consent__content">
              <div className="ess-cookie-consent__category">
                <div className="ess-cookie-consent__category-header">
                  <Checkbox
                    label={intl.formatMessage(messages.technicallyRequired)}
                    checked={true}
                    disabled={true}
                    className="ess-cookie-consent__checkbox"
                  />
                </div>
                <p className="ess-cookie-consent__category-description">
                  {intl.formatMessage(messages.technicalCookiesDescription)}
                </p>
              </div>

              <div className="ess-cookie-consent__category">
                <div className="ess-cookie-consent__category-header">
                  <Checkbox
                    label={intl.formatMessage(messages.analyticsTracking)}
                    checked={analyticsEnabled}
                    onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                    className="ess-cookie-consent__checkbox"
                  />
                </div>
                <RichText className="ess-cookie-consent__category-description">
                  {intl.formatMessage(messages.analyticsCookiesDescription)}
                </RichText>
                {googleAnalyticsId && (
                  <p className="ess-cookie-consent__category-info">
                    <small>Google Analytics ID: {googleAnalyticsId}</small>
                  </p>
                )}
              </div>
            </div>

            <div className="ess-cookie-consent__actions">
              <Button
                variant="accent"
                onClick={() => setShowSettings(false)}
                className="ess-cookie-consent__button"
              >
                {intl.formatMessage(messages.back)}
              </Button>
              <Button
                variant="positive"
                onClick={handleSavePreferences}
                className="ess-cookie-consent__button"
              >
                {intl.formatMessage(messages.savePreferences)}
              </Button>
            </div>
          </ContentWrapper>
        )}
      </PageSection>
    </aside>
  );
};

export default CookieConsent;
